import { format } from 'date-fns';

/**
 * Business-hours-aware end-time calculation, extracted verbatim from
 * CreateScheduleEditor so every scheduling surface (manual editor, schedule
 * picker, AI assistant) stretches long services across working days the same
 * way: a 30h service starting Mon 9am with 9-5 hours ends Thu ~3pm — closed
 * days are skipped, each day contributes open→close hours.
 */

export interface BusinessDayHours {
  enabled?: boolean;
  closed?: boolean;
  open: string;
  close: string;
}

export type BusinessHours = Partial<Record<
  'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday',
  BusinessDayHours
>>;

const timeToDecimalHours = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours + minutes / 60;
};

/**
 * Walk business hours forward from the start until the duration is used up.
 * Returns the start unchanged when there are no business hours or no duration
 * (same contract as the original editor implementation — callers decide their
 * own fallback).
 */
export function calculateEndAcrossBusinessHours(
  businessHours: BusinessHours | null | undefined,
  startDate: string,
  startTime: string,
  durationHours: number,
): { date: string; time: string } {
  if (!businessHours || durationHours <= 0) {
    return { date: startDate, time: startTime };
  }

  const startDateTime = new Date(`${startDate}T${startTime}`);
  let remainingHours = durationHours;
  const currentDateTime = new Date(startDateTime);
  // Hard cap so a fully-closed schedule can't loop forever
  let safety = 3660;

  const getDayHours = (date: Date) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const daySchedule = businessHours[days[date.getDay()]];
    if (!daySchedule?.enabled || daySchedule.closed) return null;
    return {
      open: timeToDecimalHours(daySchedule.open),
      close: timeToDecimalHours(daySchedule.close),
    };
  };

  while (remainingHours > 0 && safety-- > 0) {
    const dayHours = getDayHours(currentDateTime);

    // Skip to next day if business is closed
    if (!dayHours) {
      currentDateTime.setDate(currentDateTime.getDate() + 1);
      currentDateTime.setHours(0, 0, 0, 0);
      continue;
    }

    if (currentDateTime.getTime() === startDateTime.getTime()) {
      // First day: count from the (possibly mid-day) start time
      const currentTime = timeToDecimalHours(format(currentDateTime, 'HH:mm'));
      const effectiveStartTime = Math.max(currentTime, dayHours.open);
      const hoursAvailableToday = dayHours.close - effectiveStartTime;
      const hoursToApply = Math.min(remainingHours, hoursAvailableToday);

      if (hoursToApply > 0) {
        remainingHours -= hoursToApply;
        const endTime = effectiveStartTime + hoursToApply;
        currentDateTime.setHours(0, 0, 0, 0);
        currentDateTime.setTime(currentDateTime.getTime() + endTime * 60 * 60 * 1000);
      }
    } else {
      // Subsequent days: full open→close window available
      const hoursAvailableToday = dayHours.close - dayHours.open;
      const hoursToApply = Math.min(remainingHours, hoursAvailableToday);

      currentDateTime.setHours(0, 0, 0, 0);
      currentDateTime.setTime(currentDateTime.getTime() + dayHours.open * 60 * 60 * 1000);

      if (hoursToApply > 0) {
        remainingHours -= hoursToApply;
        currentDateTime.setTime(currentDateTime.getTime() + hoursToApply * 60 * 60 * 1000);
      }
    }

    if (remainingHours > 0) {
      currentDateTime.setDate(currentDateTime.getDate() + 1);
      currentDateTime.setHours(0, 0, 0, 0);
    }
  }

  return {
    date: format(currentDateTime, 'yyyy-MM-dd'),
    time: format(currentDateTime, 'HH:mm'),
  };
}
